import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CloudUpload, Package2, Trash2, RadioTower, Copy, Info, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

const SEMVER_RE = /^\d+\.\d+\.\d+(?:[-.][0-9a-zA-Z.-]+)?$/;

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let v = Number(n);
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function UpdatesPage() {
  const qc = useQueryClient();
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const { data: releases = [], isLoading } = useQuery({
    queryKey: ["updates", "releases"],
    queryFn: async () => (await api.get("/updates/releases?platform=win")).data,
  });

  const publishMut = useMutation({
    mutationFn: async () => {
      if (!SEMVER_RE.test(version)) throw new Error("Versión inválida (ej. 1.2.3 o 1.2.3-beta.1)");
      if (!file) throw new Error("Selecciona un archivo .exe");
      if (!file.name.toLowerCase().endsWith(".exe")) throw new Error("Solo se aceptan archivos .exe");
      const fd = new FormData();
      fd.append("version", version.trim());
      fd.append("platform", "win");
      fd.append("release_notes", notes);
      fd.append("file", file, file.name);
      setProgress(0);
      const res = await api.post("/updates/publish", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Release publicada — ya está disponible como current");
      qc.invalidateQueries({ queryKey: ["updates", "releases"] });
      setVersion(""); setNotes(""); setFile(null); setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (e) => {
      setProgress(null);
      toast.error(e?.response?.data?.detail || e.message || "Error al publicar");
    },
  });

  const setCurrentMut = useMutation({
    mutationFn: async (id) => (await api.post(`/updates/releases/${id}/set-current`)).data,
    onSuccess: () => { toast.success("Marcada como current"); qc.invalidateQueries({ queryKey: ["updates", "releases"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo cambiar"),
  });

  const hideMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/updates/releases/${id}`)).data,
    onSuccess: () => { toast.success("Release ocultada"); qc.invalidateQueries({ queryKey: ["updates", "releases"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo ocultar"),
  });

  const publicYamlUrl = `${window.location.origin}/api/updates/win/latest.yml`;
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(publicYamlUrl); toast.success("URL copiada"); } catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="space-y-4" data-testid="updates-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <CloudUpload size={22} className="text-primary" /> Actualizaciones del cliente
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Publica nuevas versiones del ejecutable Windows (.exe). Cada wrapper Electron consulta este
            portal y se actualiza automáticamente.
          </p>
        </div>
        <Badge variant="outline" className="font-mono">{releases.length} publicadas</Badge>
      </div>

      <Card className="border-border/70 p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <RadioTower size={14} />
          <span className="uppercase tracking-widest">URL pública</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-xs md:text-sm font-mono bg-secondary/30 rounded px-2 py-1.5 border border-border/60 break-all flex-1">
            {publicYamlUrl}
          </code>
          <Button variant="outline" size="sm" onClick={copyUrl} data-testid="updates-copy-yml-url">
            <Copy size={14} className="mr-2" /> Copiar
          </Button>
        </div>
        <div className="mt-2 text-[11px] text-muted-foreground flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            Esta URL es la que consume `electron-builder` (provider: <code className="font-mono">generic</code>).
            Ya está configurada en <code className="font-mono">/desktop/electron-builder.yml</code>.
          </span>
        </div>
      </Card>

      <Card className="border-border/70 p-4">
        <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
          <Package2 size={16} /> Publicar nueva versión
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="upd-version" className="text-[11px] uppercase tracking-widest text-muted-foreground">Versión (semver)</Label>
            <Input
              id="upd-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0 o 1.0.0-beta.1"
              data-testid="updates-version-input"
              className={version && !SEMVER_RE.test(version) ? "border-rose-500/60" : ""}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="upd-file" className="text-[11px] uppercase tracking-widest text-muted-foreground">Binario .exe</Label>
            <Input
              id="upd-file"
              ref={fileRef}
              type="file"
              accept=".exe"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="updates-file-input"
            />
            {file && (
              <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                {file.name} · {formatBytes(file.size)}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3">
          <Label htmlFor="upd-notes" className="text-[11px] uppercase tracking-widest text-muted-foreground">Notas de la versión (opcional)</Label>
          <Textarea
            id="upd-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Correcciones, novedades, mejoras…"
            data-testid="updates-notes-input"
          />
        </div>
        {progress !== null && (
          <div className="mt-3 rounded-lg border border-border/60 bg-secondary/30 p-2">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
              Subiendo {progress}%
            </div>
            <div className="h-1.5 bg-border/60 rounded overflow-hidden">
              <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
        <div className="mt-3 flex items-center justify-end">
          <Button
            onClick={() => publishMut.mutate()}
            disabled={!version || !file || publishMut.isPending}
            data-testid="updates-publish-button"
          >
            {publishMut.isPending ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Publicando…</>
            ) : (
              <><CloudUpload size={14} className="mr-2" /> Publicar</>
            )}
          </Button>
        </div>
      </Card>

      <Card className="border-border/70 p-0 overflow-hidden">
        <div className="p-3 border-b border-border/60 flex items-center gap-2">
          <Package2 size={16} className="text-muted-foreground" />
          <h2 className="text-base font-semibold">Historial (Windows)</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="updates-releases-table">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3">Versión</th>
                <th className="text-left py-2 px-3">Archivo</th>
                <th className="text-right py-2 px-3">Tamaño</th>
                <th className="text-left py-2 px-3">Publicada</th>
                <th className="text-left py-2 px-3">Por</th>
                <th className="text-center py-2 px-3">Estado</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Cargando…</td></tr>
              )}
              {!isLoading && releases.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-muted-foreground" data-testid="updates-empty">Aún no hay releases publicadas.</td></tr>
              )}
              {releases.map((r) => (
                <tr key={r.id} className="border-b border-border/60" data-testid={`updates-row-${r.version}`}>
                  <td className="py-2 px-3 font-mono font-semibold">{r.version}</td>
                  <td className="py-2 px-3 font-mono text-xs">{r.filename}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{formatBytes(r.size)}</td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">
                    {new Date(r.published_at).toLocaleString("es-VE")}
                  </td>
                  <td className="py-2 px-3 text-xs">{r.published_by || "—"}</td>
                  <td className="py-2 px-3 text-center">
                    {r.is_current ? (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40" data-testid={`updates-current-${r.version}`}>
                        <Check size={12} className="mr-1" /> current
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">histórico</Badge>
                    )}
                  </td>
                  <td className="py-2 px-3 whitespace-nowrap">
                    <div className="flex items-center gap-1 justify-end">
                      {!r.is_current && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setCurrentMut.mutate(r.id)}
                          data-testid={`updates-set-current-${r.version}`}
                        >
                          Marcar current
                        </Button>
                      )}
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => {
                          if (confirm(`¿Ocultar la versión ${r.version}? Los wrappers dejarán de recibirla.`)) hideMut.mutate(r.id);
                        }}
                        data-testid={`updates-hide-${r.version}`}
                        title="Ocultar"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
